import { DataRow, IdentifierColumn, SortDirection, SortingInfo, SortType } from '../types';
import { ProviderCollectionProps } from './types';

const REGION_NAME_GROUPING = 'region_name';
const REVENUE_DECOMPOSITION_ENDPOINT = 'revenue-decomposition';

function getNumericValueFromCell(row: DataRow, cellName: string, sortIndex: number): number | null {
  const actualValue = getValueFromRow(row, cellName, sortIndex);
  if (actualValue === null) {
    return null;
  }
  return isNaN(Number(actualValue)) ? null : Number(actualValue);
}

export function getValueFromRow(row: DataRow, cellName: string, sortIndex: number): number | string | null {
  const value = row.values[cellName];
  if (typeof value === 'number' || typeof value === 'string') {
    return value;
  } else if (Array.isArray(value)) {
    return value[sortIndex];
  }
  return null;
}

export function sortRows(row1: DataRow, row2: DataRow, sorting: SortingInfo): number {
  const { columnName, direction, sortKey, sortType, sortIndex } = sorting;
  const fieldName = sortKey || columnName;

  switch (sortType) {
    case SortType.Alpha:
      return compareAlpha(row1, row2, fieldName, direction);
    case SortType.Date:
      return compareDate(row1, row2, fieldName, direction);
    default:
      return compareNumeric(row1, row2, fieldName, direction, sortIndex);
  }
}

function compareAlpha(row1: DataRow, row2: DataRow, fieldName: string, direction: SortDirection): number {
  const sortKeys = fieldName.split('|');
  const value1 = getConcatenatedValues(row1, sortKeys);
  const value2 = getConcatenatedValues(row2, sortKeys);

  return direction === SortDirection.ascending ? value1.localeCompare(value2) : value2.localeCompare(value1);
}

function getConcatenatedValues(row: DataRow, keys: string[]): string {
  return keys.reduce((acc, key) => acc + String(row.values[key] ?? ''), '');
}

function compareDate(row1: DataRow, row2: DataRow, fieldName: string, direction: SortDirection): number {
  const value1 = new Date(String(row1.values[fieldName]));
  const value2 = new Date(String(row2.values[fieldName]));

  if (value1.getTime() === value2.getTime()) return 0;

  return sortWithDirection(value1, value2, direction);
}

function sortWithDirection<T>(value1: T, value2: T, direction: SortDirection): number {
  let result = 0;

  if (value1 > value2) {
    result = 1;
  } else if (value1 < value2) {
    result = -1;
  }

  return direction === SortDirection.descending ? -result : result;
}

function compareNumeric(
  row1: DataRow,
  row2: DataRow,
  fieldName: string,
  direction: SortDirection,
  sortIndex?: number
): number {
  const value1 = getNumericValueFromCell(row1, fieldName, sortIndex) || 0;
  const value2 = getNumericValueFromCell(row2, fieldName, sortIndex) || 0;

  if (value1 === value2) return 0;

  return sortWithDirection(value1, value2, direction);
}

export function getIdentifiers(
  idColumn: IdentifierColumn,
  row: DataRow,
  isInfoRow = false
): {
  id: string;
  name: string;
  description: string;
  identifiers: string[];
} {
  const { identifier, subIdentifier, idField } = idColumn;

  let idTexts: string[] = [];
  identifier?.forEach(field => {
    const value = row.values[field];
    if (isInfoRow && field === 'brand_description' && String(value) !== 'Tradebrand') {
      return;
    }
    if (value) {
      idTexts.push(String(value));
    }
  });

  if (idTexts.length === 0) {
    idTexts.push(String(row.values[idField] || '-'));
  }

  let subTexts: string[] = [];
  if (subIdentifier) {
    subIdentifier.forEach(field => {
      const value = row.values[field];
      if (value) {
        subTexts.push(String(value));
      }
    });
  }

  return {
    id: String(row.values[idField]),
    name: idTexts.join(' ').trim(),
    description: subTexts.join(' ').trim(),
    identifiers: identifier,
  };
}

export const checkIfMultiConfigError = (
  paramsForData: Record<string, string[]>,
  endpoint: string,
  collection: ProviderCollectionProps
): boolean => {
  const isCollection = Object.keys(collection || {}).length > 0;
  const hasMultipleCells = paramsForData?.cell?.length > 1;
  const hasRegionGrouping = paramsForData?.grouping?.some(group => group?.includes(REGION_NAME_GROUPING));
  const isRevenueEndpoint = endpoint === REVENUE_DECOMPOSITION_ENDPOINT;

  return isCollection && hasMultipleCells && (hasRegionGrouping || isRevenueEndpoint);
};
